// src/components/auth/PasswordReset.tsx
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { useT } from '../../i18n/useT';

interface PasswordResetProps {
  onBack: () => void;
}

export const PasswordReset: React.FC<PasswordResetProps> = ({ onBack }) => {
  const t = useT();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { resetPassword, error, clearError } = useAuthStore();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!email.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      clearError();
      
      await resetPassword(email.trim());
      setIsSuccess(true);
    } catch (error) {
      // Error is handled by the store
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = email.trim();

  if (isSuccess) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 3 }}>
          
          {t("Password reset email sent! Check your inbox and follow the instructions to reset your password.")}
        </Alert>
        
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBack />}
          fullWidth
        >
          
          {t("Back to Sign In")}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        variant="text"
        onClick={onBack}
        startIcon={<ArrowBack />}
        sx={{ mb: 2 }}
      >
        
        {t("Back to Sign In")}
      </Button>

      <Typography variant="h6" gutterBottom>
        
        {t("Reset Your Password")}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        
        {t("Enter your email address and we'll send you a link to reset your password.")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          type="email"
          label={t("Email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          margin="normal"
          required
          autoComplete="email"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!isFormValid || isSubmitting}
          sx={{ mt: 3, mb: 2, py: 1.5 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            'Send Reset Email'
          )}
        </Button>
      </Box>
    </Box>
  );
};